// Calculate exponential or polynomial regression for trend line
// Fits data to y = a * e^(b * x) for exponential or y = ax^2 + bx + c for polynomial
export interface CmgrDetails {
    method: 'regression' | 'trailing' | 'none';
    cmgr: number;
    dataPoints: number;          // total non-zero months used
    windowSize: number;          // how many months in the calculation window
    firstDate: string;
    lastDate: string;
    firstValue: number;
    lastValue: number;
    periods: number;             // calendar months spanned
}

export function calculateTrendLine(data: { date: string; count: number; isProjected?: boolean; hideFromGraph?: boolean }[]): {
    type: 'exponential' | 'polynomial';
    a: number;
    b: number;
    c?: number;
    startIndex?: number;
    exponentialR2?: number;
    linearR2?: number;
    cmgr?: number;
    cmgrDetails?: CmgrDetails;
} | null {
    // Exclude projected data points, zero/negative counts, and hidden data
    const filteredData = data.filter(d => d.count > 0 && !d.isProjected && !d.hideFromGraph);
    
    console.log('Calculating trend for data:', {
        totalPoints: data.length,
        filteredPoints: filteredData.length,
        firstPoint: filteredData[0],
        lastPoint: filteredData[filteredData.length - 1],
        hasProjected: data.some(d => d.isProjected)
    });
    
    // Need at least 4 data points for meaningful trend
    if (filteredData.length < 4) return null;
    
    // Check if there's actual variation in the data
    const firstPoint = filteredData[0].count;
    const lastPoint = filteredData[filteredData.length - 1].count;
    const maxPoint = Math.max(...filteredData.map(d => d.count));
    const minPoint = Math.min(...filteredData.map(d => d.count));
    
    // If there's no significant variation, don't show a trend line
    if (maxPoint - minPoint < 5) {
        console.log('No significant variation in data:', { min: minPoint, max: maxPoint, diff: maxPoint - minPoint });
        return null;
    }
    
    // Calculate Compound Monthly Growth Rate (CMGR)
    // Strategy adapts based on data maturity:
    //   - Robust datasets (12+ months): regression-based rate (smooths month-to-month noise)
    //   - Smaller/newer accounts (<12 months): trailing window endpoint CMGR
    // Calculate Compound Monthly Growth Rate (CMGR)
    // Standard formula: CMGR = (Ending Value / Beginning Value)^(1/n) - 1
    // Adapts window size based on data maturity:
    //   - Mature datasets (12+ months): full range (after ramp-up filtering)
    //   - Smaller/newer accounts (<12 months): trailing 6-month window
    let cmgr = 0;
    let cmgrMethod: 'regression' | 'trailing' | 'none' = 'none';
    let cmgrDetails: CmgrDetails | undefined;
    if (filteredData.length >= 2) {
        const MATURE_THRESHOLD = 12;
        const TRAILING_WINDOW = 6;

        // Choose window: mature datasets use full range, newer ones use trailing window
        let windowData = filteredData;
        const method: 'regression' | 'trailing' = filteredData.length >= MATURE_THRESHOLD ? 'regression' : 'trailing';

        if (method === 'trailing' && filteredData.length > TRAILING_WINDOW) {
            windowData = filteredData.slice(-TRAILING_WINDOW);
        }

        // Skip early ramp-up points that are < 20% of the median value
        // These distort CMGR for accounts that started from nearly zero
        const sortedCounts = [...windowData.map(d => d.count)].sort((a, b) => a - b);
        const median = sortedCounts[Math.floor(sortedCounts.length / 2)];
        const rampUpThreshold = median * 0.2;
        while (windowData.length > 2 && windowData[0].count < rampUpThreshold) {
            windowData = windowData.slice(1);
        }

        const firstValue = windowData[0].count;
        const lastValue = windowData[windowData.length - 1].count;
        const firstName = windowData[0].date;
        const lastName = windowData[windowData.length - 1].date;

        // Calculate actual calendar months between first and last dates
        const firstIdx = data.findIndex(d => d.date === firstName);
        const lastIdx = data.findIndex(d => d.date === lastName);
        const periods = lastIdx - firstIdx;

        if (firstValue > 0 && lastValue > 0 && periods > 0) {
            // Standard CMGR formula: (Ending / Beginning)^(1/n) - 1
            cmgr = Math.pow(lastValue / firstValue, 1 / periods) - 1;
            cmgrMethod = method;
            cmgrDetails = {
                method,
                cmgr,
                dataPoints: filteredData.length,
                windowSize: windowData.length,
                firstDate: firstName,
                lastDate: lastName,
                firstValue,
                lastValue,
                periods,
            };
            console.log(`CMGR calculation (${method}):`, {
                firstValue,
                lastValue,
                periods,
                firstDate: firstName,
                lastDate: lastName,
                windowSize: windowData.length,
                fullDataPoints: filteredData.length,
                cmgr: (cmgr * 100).toFixed(2) + '%'
            });
        }
    }
    
    // Try exponential regression on all data
    // Using linearization: ln(y) = ln(a) + b*x
    const n = filteredData.length;
    let sumX = 0, sumX2 = 0;
    let sumLnY = 0, sumXLnY = 0;
    let canUseExponential = true;
    
    // First pass to check if exponential is viable and calculate sums
    for (let i = 0; i < filteredData.length; i++) {
        const x = i;
        const y = filteredData[i].count;
        
        if (y <= 0) {
            canUseExponential = false;
            break;
        }
        
        const lnY = Math.log(y);
        sumX += x;
        sumX2 += x * x;
        sumLnY += lnY;
        sumXLnY += x * lnY;
    }
    
    // Calculate mean for R-squared calculations
    const meanY = filteredData.reduce((sum, p) => sum + p.count, 0) / n;
    let ssTot = 0;
    filteredData.forEach(point => {
        ssTot += Math.pow(point.count - meanY, 2);
    });
    
    let exponentialR2 = 0;
    let exponentialA = 0;
    let exponentialB = 0;
    
    if (canUseExponential && n >= 2) {
        // Calculate exponential regression parameters
        const b = (n * sumXLnY - sumX * sumLnY) / (n * sumX2 - sumX * sumX);
        const lnA = (sumLnY - b * sumX) / n;
        const a = Math.exp(lnA);
        
        exponentialA = a;
        exponentialB = b;
        
        // Calculate R-squared for exponential fit in log space
        // Since we fit ln(y) = ln(a) + bx, calculate R² for the linearized model
        const meanLnY = sumLnY / n;
        let ssResLn = 0;
        let ssTotLn = 0;
        
        filteredData.forEach((point, index) => {
            const lnY = Math.log(point.count);
            const predictedLnY = lnA + b * index;
            ssResLn += Math.pow(lnY - predictedLnY, 2);
            ssTotLn += Math.pow(lnY - meanLnY, 2);
        });
        
        exponentialR2 = ssTotLn === 0 ? 0 : 1 - (ssResLn / ssTotLn);
        
        console.log('Exponential fit details:', {
            a: a.toFixed(4),
            b: b.toFixed(5),
            exponentialR2: exponentialR2.toFixed(5),
            lnA: lnA.toFixed(5),
            meanLnY: meanLnY.toFixed(5),
            ssResLn: ssResLn.toFixed(5),
            ssTotLn: ssTotLn.toFixed(5),
            sumX,
            sumX2,
            sumLnY: sumLnY.toFixed(5),
            sumXLnY: sumXLnY.toFixed(5),
            n
        });
    }
    
    // Calculate linear regression
    sumX = 0;
    sumX2 = 0;
    let sumY = 0, sumXY = 0;
    
    filteredData.forEach((point, index) => {
        const x = index;
        const y = point.count;
        sumX += x;
        sumY += y;
        sumXY += x * y;
        sumX2 += x * x;
    });
    
    const denominator = (n * sumX2 - sumX * sumX);
    if (Math.abs(denominator) < 0.0001) {
        return null; // Can't calculate trend line
    }
    
    const slope = (n * sumXY - sumX * sumY) / denominator;
    const intercept = (sumY - slope * sumX) / n;
    
    // Calculate R-squared for linear fit
    let ssResLin = 0;
    filteredData.forEach((point, index) => {
        const predictedY = slope * index + intercept;
        const actualY = point.count;
        ssResLin += Math.pow(actualY - predictedY, 2);
    });
    
    const linearR2 = ssTot === 0 ? 0 : 1 - (ssResLin / ssTot);
    
    // Decide which model to use based on R² values and growth
    console.log('R² values:', { exponentialR2, linearR2, dataPoints: filteredData.length });
    
    // Use exponential if it fits well and shows meaningful growth
    if (exponentialR2 > 0.7 && exponentialB > 0.01 && exponentialR2 > linearR2) {
        return { 
            type: 'exponential', 
            a: exponentialA, 
            b: exponentialB,
            exponentialR2,
            linearR2,
            cmgr,
            cmgrDetails
        };
    }
    
    // Use linear if it has meaningful slope
    if (Math.abs(slope) > 0.1 && linearR2 > 0.5) {
        return {
            type: 'polynomial',
            a: 0,
            b: slope,
            c: intercept,
            exponentialR2,
            linearR2,
            cmgr,
            cmgrDetails
        };
    }

    // Even if no trend line is good enough, return CMGR if we have enough data
    if (filteredData.length >= 2 && cmgr !== 0) {
        return {
            type: 'polynomial',
            a: 0,
            b: 0,
            c: 0,
            exponentialR2,
            linearR2,
            cmgr,
            cmgrDetails
        };
    }

    return null; // No meaningful trend detected
}

// Generate trend line data points
export function generateTrendLineData(
    groupedData: any[],
    trend: { type: 'exponential' | 'polynomial'; a: number; b: number; c?: number; startIndex?: number } | null
): any[] | null {
    if (!trend) return null;
    
    if (trend.type === 'exponential') {
        // Generate exponential trend line: y = a * e^(b * x)
        return groupedData.map((point, index) => ({
            ...point,
            trend: Math.max(0, trend.a * Math.exp(trend.b * index))
        }));
    } else {
        // Generate polynomial trend line: y = ax^2 + bx + c
        return groupedData.map((point, index) => ({
            ...point,
            trend: Math.max(0, trend.a * index * index + trend.b * index + (trend.c || 0))
        }));
    }
}